

























































import Vue from 'vue';
import { ReportSpec } from '@/components/ReportControls.vue';

import { mdiArrowUpThick, mdiArrowDownThick } from '@mdi/js';
import Icon from '@/components/Icon.vue';

const NUMBER_FORMAT = Intl.NumberFormat('en-GB', { style: 'decimal' });
const CURRENCY_FORMAT = Intl.NumberFormat('en-GB', {/*style: 'currency', currency: 'GBP',*/
  maximumFractionDigits: 0,
});

export interface ColumnConfig<T extends string | number> {
  format?: 'number' | 'currency' | 'percent' | ((raw: T, row: T[]) => string);
  aggregate?: 'total' | 'mean' | 'none' | ((values: T[], otherAggregates: T[]) => T);
  decimalPlaces?: number;
  hidden?: boolean;
}

interface SortAsc {
  direction: 'ASC';
  index: number;
}

interface SortDesc {
  direction: 'DESC';
  index: number;
}

export type SortSpec = undefined | SortDesc | SortAsc;

export default Vue.extend({
  name: 'ReportTable',
  props: {
    sortInitial: Object as () => SortSpec,
    spec: Object as () => ReportSpec,
    headers: Array as () => string[],
    data: Array as () => Array<Array<string | number>>,
    dataIncludesTotals: {
      type: Boolean,
      required: false,
      default: false,
    },
    columnConfig: {
      type: Array as () => Array<ColumnConfig<string> | ColumnConfig<number>>,
      default: (): Array<ColumnConfig<string> | ColumnConfig<number>> => [],
    },
    formatByRow: {
      type: Boolean,
      default: false,
    },
    rowConfig: {
      type: Array as () => Array<ColumnConfig<string> | ColumnConfig<number>>,
      default: (): Array<ColumnConfig<string> | ColumnConfig<number>> => [],
    },
    totalRows: {
      type: Boolean,
      default: true,
    },
    totalColumns: {
      type: Boolean,
      default: true,
    },
    // A dictionary of header label: functions that take the row and return a number
    extraAggregateColumns: {
      default: () => {
        return {} as { [header: string]: (row: Array<string | number>) => string | number };
      },
      type: Object as () => { [header: string]: (row: Array<string | number>) => string | number },
    },
    canBreakdown: {
      type: Boolean,
      default: false,
    },
    breakdowns: {
      type: Object as () => { [key: string]: Array<Array<string | number>> },
      default: () => {
        return {};
      },
    },
  },
  components: { Icon },
  data() {
    return {
      sort: undefined as SortSpec,
      sortAscIcon: mdiArrowUpThick,
      sortDescIcon: mdiArrowDownThick,
    };
  },
  computed: {
    sortedData(): Array<Array<string | number>> {
      if (this.sort === undefined) {
        return this.augmentedRows;
      } else {
        return Array.from(this.augmentedRows).sort((a, b) => {
          const aVal = a.filter((_, i) => !this.colConfig(i).hidden)[this.sort!.index];
          const bVal = b.filter((_, i) => !this.colConfig(i).hidden)[this.sort!.index];
          const dir = this.sort!.direction === 'ASC' ? 1 : -1;
          if (typeof (aVal) === 'number' && isNaN(aVal)) {
            return -dir;
          }
          if (typeof (bVal) === 'number' && isNaN(bVal)) {
            return dir;
          }
          if (aVal > bVal) {
            return dir;
          } else if (aVal < bVal) {
            return -dir;
          } else {
            return 0;
          }
        });
      }
    },
    augmentedHeaders(): string[] {
      return this.headers.concat(
          Object.getOwnPropertyNames(this.extraAggregateColumns).filter((h) => !h.startsWith('__')),
          this.totalRows ? ['Total'] : [],
      );
    },
    augmentedRows(): Array<Array<string | number>> {
      const rows = this.dataIncludesTotals ? this.data.slice(0, this.data.length - 1) : this.data;
      return rows.map((r) => {
        const measures = r.slice(1) as number[];
        return r.concat(
            Object.values(this.extraAggregateColumns)
                .map((f) => f(measures)),
            this.totalRows ? [measures.reduce((a, b) => a + b, 0)] : [],
        );
      });
    },
    augmentedBreakdowns(): { [key: string]: Array<Array<string | number>> } {
      return Object.fromEntries(
          Object.entries(this.breakdowns).map(([key, rs]) => {
            const augmentedRows = rs.map((r) => {
              const measures = r.slice(1) as number[];
              return r.concat(
                  Object.values(this.extraAggregateColumns)
                      .map((f) => f(measures)),
                  this.totalRows ? [measures.reduce((a, b) => a + b, 0)] : [],
              );
            });
            return [key, augmentedRows];
          }),
      );
    },
    columnTotals(): Array<string | number> {
      // const dataColumns: number[][] = this.data.map((r) => r.slice(1) as number[]);
      if (this.dataIncludesTotals) {
        // Just read 'em from the array
        return this.data[this.data.length - 1].slice(1);
      }
      const aggregates: number[] = [];
      const columns = this.headers.slice(1).map(() => [] as number[]);
      this.data.forEach((row) => row.filter((_, i) => !this.colConfig(i).hidden).slice(1).forEach((col, i) => {
        columns[i].push(col as number);
      }));
      for (let i = 0; i < columns.length; ++i) {
        switch (this.colConfig(i + 1).aggregate ?? 'total') {
          case 'total':
            aggregates.push((columns[i] as number[]).reduce((a, b) => a + b, 0));
            break;
          case 'mean':
            aggregates.push((columns[i] as number[]).reduce((a, b) => a + b, 0) / this.data.length);
            break;
          case 'none':
            aggregates.push(NaN);
            break;
          default:
            const aggregate = this.colConfig(i + 1).aggregate as (vals: number[], otherAggreates: number[]) => number;
            aggregates.push(aggregate(columns[i] as number[], aggregates));
            break;
        }
      }
      const summaries: Array<string | number> = [];
      for (const h of Object.getOwnPropertyNames(this.extraAggregateColumns)) {
        const f = this.extraAggregateColumns[h];
        if (typeof f === 'function') {
          summaries.push(f(aggregates));
        }
      }
      if (this.totalRows) {
        summaries.push(aggregates.reduce((a, b) => a + b, 0));
      }
      return (aggregates as Array<string | number>).concat(summaries);
    },
  },
  methods: {
    colConfig(colIndex: number): ColumnConfig<string> | ColumnConfig<number> {
      return this.columnConfig[colIndex] ?? {};
    },
    getRowConfig(rowIndex: number): ColumnConfig<string> | ColumnConfig<number> {
      return this.rowConfig[rowIndex] ?? {};
    },
    colClasses(colIndex: number) {
      const classes = [`column-${colIndex}`];
      const columnCount = this.headers.length + Object.getOwnPropertyNames(this.extraAggregateColumns).length;
      if (this.totalRows && colIndex === (columnCount - 1)) {
        classes.push('total');
      }
      return classes;
    },
    format(val: string | number, row: Array<string | number>, colIndex: number, rowIndex: number): string {
      if (typeof val === 'string') {
        return val;
      }
      const f = this.formatByRow ? this.getRowConfig(rowIndex) : this.colConfig(colIndex);
      switch (f.format ?? 'number') {
        case 'number':
          return NUMBER_FORMAT.format(val);
        case 'currency':
          return Intl.NumberFormat('en-GB', {
            minimumFractionDigits: f.decimalPlaces ?? 0,
            maximumFractionDigits: f.decimalPlaces ?? 0,
          }).format(val);
        case 'percent':
          const formatter = Intl.NumberFormat('en-GB', {
            style: 'percent',
            minimumFractionDigits: f.decimalPlaces ?? 0,
            maximumFractionDigits: f.decimalPlaces ?? 0,
          });
          return formatter.format(val / 100);
        default:
          return (f.format as (raw: number, row: Array<string | number>) => string)(val, row);
      }
    },
    headerClick(index: number) {
      if (this.sort === undefined || this.sort.index !== index) {
        this.sort = { index, direction: 'DESC' };
      } else if (this.sort.direction === 'DESC') {
        this.sort = { index, direction: 'ASC' };
      } else {
        this.sort = undefined;
      }
    },
    breakdownClick(payload: string) {
      if (this.canBreakdown) {
        this.$emit('breakdown', { key: payload, sort: this.sort });
      }
    },
  },
  mounted() {
    this.sort = this.sortInitial;
  },
  watch: {
    sort() {
      // Reload any breakdowns to pick up the new sort order
      for (const bd of Object.keys(this.breakdowns)) {
        // First, remove the old breakdowm
        this.breakdownClick(bd);
        // Now, request it afresh
        this.breakdownClick(bd);
      }
    },
  },
});
